import { motion } from "framer-motion";

const features = [
  { icon: "🥕", title: "Fresh Produce", desc: "Local" },
  { icon: "🎨", title: "Artisan Goods", desc: "Handcrafted Items" },
  { icon: "🎵", title: "Live Events", desc: "Music & Workshops" },
  { icon: "🤝", title: "Community", desc: "Meet Your Neighbours" },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

export default function WeeklySaturdayMarket() {
  return (
    <section
      style={{
        background:
          "linear-gradient(135deg, #0a2605 0%, #175c0c 45%, #0d3b07 100%)",
        padding: "clamp(56px, 9vw, 100px) 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Large decorative background text */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "clamp(120px, 25vw, 240px)",
          fontWeight: 900,
          color: "rgba(255,255,255,0.03)",
          fontFamily: "'Montserrat', sans-serif",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          userSelect: "none",
          letterSpacing: "-8px",
          zIndex: 0,
        }}
      >
        SATURDAY
      </div>

      {/* Glow blobs */}
      <div className="wsm-blob wsm-blob-1" />
      <div className="wsm-blob wsm-blob-2" />

      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: "0 clamp(16px, 4vw, 32px)",
          position: "relative",
          zIndex: 1,
          textAlign: "center",
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          {/* Badge */}
          <motion.div variants={itemVariants}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "999px",
                padding: "9px 22px",
                color: "#fff",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "0.78rem",
                letterSpacing: "2px",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}
            >
              <span className="wsm-dot" />
              Year-Round Community Market
            </span>
          </motion.div>

          {/* Main heading */}
          <motion.h2
            variants={itemVariants}
            style={{
              fontSize: "clamp(2.8rem, 8vw, 5.5rem)",
              fontWeight: 900,
              color: "#fff",
              fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
              letterSpacing: "-2px",
              margin: "0 0 clamp(24px, 4vw, 36px)",
              lineHeight: 1,
              textShadow: "0 4px 30px rgba(0,0,0,0.25)",
            }}
          >
            Every Saturday
          </motion.h2>

          {/* Time pill */}
          <motion.div
            variants={itemVariants}
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "clamp(36px, 6vw, 56px)",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "clamp(12px, 2vw, 20px)",
                background: "rgba(255,255,255,0.97)",
                padding: "clamp(18px, 3vw, 26px) clamp(28px, 5vw, 52px)",
                borderRadius: "20px",
                boxShadow:
                  "0 12px 48px rgba(0,0,0,0.2), 0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <span style={{ fontSize: "clamp(1.8rem, 4vw, 2.6rem)" }}>🕘</span>
              <div style={{ textAlign: "left" }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "#999",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    fontFamily: "'Montserrat', sans-serif",
                    marginBottom: "2px",
                  }}
                >
                  Market Hours
                </div>
                <div
                  style={{
                    fontSize: "clamp(1.4rem, 3.5vw, 2.1rem)",
                    fontWeight: 900,
                    background: "linear-gradient(90deg, #2d6a14, #6dc744)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontFamily: "'Montserrat', sans-serif",
                    letterSpacing: "-0.5px",
                    lineHeight: 1,
                  }}
                >
                  9:00 AM – 4:00 PM
                </div>
              </div>
            </div>
          </motion.div>

          {/* Feature cards */}
          <motion.div
            variants={itemVariants}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "clamp(12px, 2.5vw, 20px)",
            }}
          >
            {features.map((f, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6, scale: 1.03 }}
                transition={{ duration: 0.25 }}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "20px",
                  padding: "clamp(20px, 3vw, 28px) clamp(16px, 2.5vw, 22px)",
                  cursor: "default",
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(2rem, 4vw, 2.6rem)",
                    marginBottom: "10px",
                  }}
                >
                  {f.icon}
                </div>
                <div
                  style={{
                    fontWeight: 800,
                    color: "#fff",
                    fontSize: "clamp(0.95rem, 2vw, 1.1rem)",
                    fontFamily: "'Montserrat', sans-serif",
                    marginBottom: "4px",
                  }}
                >
                  {f.title}
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.88rem",
                    fontWeight: 500,
                  }}
                >
                  {f.desc}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Footer line */}
          <motion.p
            variants={itemVariants}
            style={{
              marginTop: "clamp(32px, 5vw, 48px)",
              color: "rgba(255,255,255,0.7)",
              fontSize: "clamp(0.95rem, 2vw, 1.15rem)",
              fontWeight: 600,
              letterSpacing: "0.3px",
            }}
          >
            ☀️ Rain or Shine — We're Here for You!
          </motion.p>
        </motion.div>
      </div>

      <style>{`
        .wsm-blob {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(80px);
        }
        .wsm-blob-1 {
          width: 400px; height: 400px;
          background: rgba(74,140,37,0.12);
          top: -120px; right: -100px;
        }
        .wsm-blob-2 {
          width: 320px; height: 320px;
          background: rgba(45,106,20,0.2);
          bottom: -100px; left: -80px;
        }
        .wsm-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #6dc744;
          display: inline-block;
          animation: wsm-pulse 2s ease-in-out infinite;
        }
        @keyframes wsm-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
      `}</style>
    </section>
  );
}
